import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Chip, Grid, LinearProgress, Skeleton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ThunderstormOutlinedIcon from '@mui/icons-material/ThunderstormOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageTitle from '../../common/PageTitle';
import AwbLinkCell from '../../common/AwbLinkCell';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { TableSkeleton } from '../../common/Skeletons';
import { apiConstants, getGeneric } from '../../common/constants';
import { dataGridWrapSx, tabBarSx } from '../../styles/patterns';
import { formatDate } from '../../helpers/UtilityHelper';
import { getMerchant } from '../../helpers/ReduxHelpers';
import {
  pageContainerSx, softCardSx, softCardTitleSx, softCardSubSx, heroKpiCardSx, heroKpiIconSx,
  FONT, SIZE, WEIGHT, NAVY, SUBTLE, BORDER, WHITE, SHADOW, PRIMARY_BLUE, SUCCESS, ERROR, WARNING,
} from '../dashboard/dashboardStyles';
import { AMBER_TEXT, TEAL } from '../../styles/foundation/colors';
import { awbTextSx, cellStackSx, getStatusChipSx, primaryTextSx, secondaryTextSx, tertiaryTextSx } from '../orders/list2/cellStyles';
import useAppointmentSearch from '../appointments/shared/useAppointmentSearch';
import ExportButton from '../appointments/shared/ExportButton';
import { NO_NEW_DATE_FILTER, OPEN_MISSED_FILTER, buildOverdueFilter } from '../appointments/shared/constants';
import { viewColumn } from './ViewOrderButton';
import usePtlApi from './usePtlApi';
import { SplitBar } from './PtlTiles';
import {
  EDD_OWNERS, EDD_OWNER_KEYS, MISS_ATTRIBUTION, MISS_ATTRIBUTION_KEYS, MISS_ATTRIBUTION_SHORT, NO_FIRST_APPOINTMENT_DAYS,
  PTL_PATHS, abdTodayFilter, errorText, fromUtc, latestMissFilter, noFirstAppointmentFilter, nonAbdTodayFilter,
} from './constants';

const DELIVERED = { field: 'orderStatus', operator: 'eq', value: 'DELIVERED' };
const NOT_DELIVERED = { field: 'orderStatus', operator: 'ne', value: 'DELIVERED' };
const DUE_LIST_SIZE = 10;
const MISSED_PATH = '/appointments/missed-repeat';
const EDD_OWNER_ICONS = {
  NONE: <HourglassEmptyIcon />, PROZO: <BusinessIcon />, CLIENT: <PersonOutlineIcon />, EXTERNAL: <ThunderstormOutlinedIcon />,
};

// One row, count only. Each tile sends the same filter as the list it opens, so the numbers match.
const countQuery = (extraAndFilter, reload, appointmentRequired = true) => ({
  extraAndFilter, appointmentRequired, page: 0, sizePerPage: 1, reload,
});
const countOf = (q) => (q.isFetching ? undefined : q.data?.count ?? 0);
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

const linkTextSx = {
  fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: PRIMARY_BLUE,
  display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
};
const metaSx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE };
const valueSx = (color) => ({
  fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: SIZE['3xl'], lineHeight: 1, color,
  fontVariantNumeric: 'tabular-nums',
});
// Plain white tile; only the "needs attention" tiles carry a tint.
const plainTileSx = {
  p: '16px', borderRadius: '16px', border: `1px solid ${BORDER}`, bgcolor: WHITE, height: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover },
};
const barSkeleton = <Skeleton variant='rectangular' height={6} sx={{ borderRadius: 3 }} />;
const footerSkeleton = <Skeleton variant='text' width={150} />;

/** Card header: title (+ optional info tooltip) and one-line subtitle, optional control on the right. */
function CardHeader({ title, info, sub, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={softCardTitleSx}>{title}</Typography>
          {info && (
            <Tooltip title={info} arrow placement='right'>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: SUBTLE, cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        {sub && <Typography sx={softCardSubSx}>{sub}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

/** "● Client 11 · ● Prozo 20 …" legend for a SplitBar. */
function Legend({ segments }) {
  return (
    <Box component='span' sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.25 }}>
      {segments.map((s) => (
        <Box component='span' key={s.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box component='span' sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: s.color }} />
          {s.label} {num(s.value)}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Clickable KPI tile: icon + label + hint on the left, the count on the right, then an optional bar and a footer
 * line. `value` / `bar` / `footer` undefined while loading show skeletons. `tint` = the pastel attention style.
 */
function Tile({ accent, valueColor, icon, label, hint, value, bar, footer, cta, onClick, tint = false, withBar = false }) {
  return (
    <ButtonBase onClick={onClick} sx={{ ...(tint ? heroKpiCardSx(accent) : plainTileSx), width: '100%', display: 'block', textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={heroKpiIconSx(accent)}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{label}</Typography>
            <Typography sx={metaSx}>{hint}</Typography>
          </Box>
        </Box>
        {value === undefined
          ? <Skeleton variant='text' width={52} sx={{ fontSize: SIZE['3xl'], lineHeight: 1 }} />
          : <Typography sx={valueSx(valueColor)}>{num(value)}</Typography>}
      </Box>
      {withBar && <Box sx={{ mt: 1.75 }}>{bar === undefined ? barSkeleton : bar}</Box>}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1.25 }}>
        <Typography component='div' sx={metaSx}>{footer === undefined && withBar ? footerSkeleton : footer}</Typography>
        <Typography sx={linkTextSx}>{cta} <ArrowForwardIcon sx={{ fontSize: 14 }} /></Typography>
      </Box>
    </ButtonBase>
  );
}

/** Bar + footer for a "today" tile: delivered vs yet to deliver. */
function deliveredParts(total, delivered) {
  if (total === undefined || delivered === undefined) return {};
  const d = Math.min(delivered, total);
  const pct = total ? Math.round((100 * d) / total) : 0;
  return {
    bar: (
      <LinearProgress variant='determinate' value={pct} aria-label={`${pct}% delivered`}
        sx={{ height: 6, borderRadius: 3, bgcolor: BORDER, '& .MuiLinearProgress-bar': { bgcolor: SUCCESS, borderRadius: 3 } }} />
    ),
    footer: total
      ? <><Box component='span' sx={{ color: SUCCESS, fontWeight: WEIGHT.semibold }}>{num(d)} delivered</Box> · {num(total - d)} yet to deliver</>
      : 'Nothing scheduled today',
  };
}

/** Bar + footer for a tile fed by a merchant API that may not be available yet. */
function apiParts(state, segments, emptyText) {
  if (state.loading) return {};
  if (state.error) return { bar: <SplitBar segments={[]} label='Not available' />, footer: state.missing ? 'Not available for this account yet' : 'Could not load' };
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  return {
    bar: <SplitBar segments={segments} label={segments.map((s) => `${s.label} ${s.value}`).join(', ')} />,
    footer: total ? <Legend segments={segments.filter((s) => s.value)} /> : emptyText,
  };
}

const dueColumns = [
  {
    field: 'awb', headerName: 'AWB / Reference', minWidth: 190, flex: 1.2, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <AwbLinkCell awb={row.awb_number} fallback='-' sx={{ ...awbTextSx, whiteSpace: 'nowrap' }} />
        {row.reference && <Typography sx={tertiaryTextSx}>Ref: {row.reference}</Typography>}
      </Box>
    ),
  },
  {
    field: 'due', headerName: 'Due', minWidth: 160, flex: 1, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={primaryTextSx}>
          {!row.dueAt ? '-' : row.appointdeliveryRequired ? formatDate(row.dueAt, 'h:mm a') : formatDate(fromUtc(row.dueAt), 'dd MMM yyyy')}
        </Typography>
        <Typography sx={tertiaryTextSx}>{row.appointdeliveryRequired ? 'Appointment slot' : 'EDD'}</Typography>
      </Box>
    ),
  },
  {
    field: 'orderStatus', headerName: 'Status', minWidth: 170, flex: 1, sortable: false,
    renderCell: ({ row }) => (row.orderStatus ? <Chip size='small' label={row.orderStatus.replace(/_/g, ' ')} sx={getStatusChipSx(row.orderStatus)} /> : '-'),
  },
  {
    field: 'courier', headerName: 'Courier', minWidth: 140, flex: 0.9, sortable: false,
    renderCell: ({ row }) => <Typography sx={primaryTextSx}>{row.courierDetail?.parent || '-'}</Typography>,
  },
  {
    field: 'destination', headerName: 'Destination', minWidth: 150, flex: 0.9, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={secondaryTextSx}>{row.delivery_details?.to_city || '-'}</Typography>
        <Typography sx={tertiaryTextSx}>{row.delivery_details?.to_pincode || ''}</Typography>
      </Box>
    ),
  },
  { field: 'view', headerName: 'View', width: 70, sortable: false, renderCell: viewColumn.renderCell },
];

/** Small heading between the tile rows of a card. */
function SubHeading({ title, sub }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mt: 2.5, mb: 1.25 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
      {sub && <Typography sx={metaSx}>{sub}</Typography>}
    </Box>
  );
}

/** Bar + footer for the ABD "missed, still open" tile: open misses split by who the latest miss is attributed to. */
function missParts(total, byOwner) {
  if (total === undefined || byOwner.some((o) => o.value === undefined)) return {};
  const recorded = byOwner.reduce((s, o) => s + o.value, 0);
  const segments = [
    ...byOwner,
    { key: 'NONE', label: 'Not recorded', value: Math.max(0, total - recorded), color: SUBTLE },
  ];
  return {
    bar: <SplitBar segments={segments} label={segments.map((s) => `${s.label} ${s.value}`).join(', ')} />,
    footer: total ? <Legend segments={segments.filter((s) => s.value)} /> : 'No open missed appointments',
  };
}

/** "Yet to deliver today" for one type: earliest due first, with a download of the whole list and the full list. */
function DueList({ abd, query, filter, listPath, fileName }) {
  const navigate = useNavigate();
  const loading = query.isFetching;
  const total = query.data?.count ?? 0;
  const rows = loading ? [] : (query.data?.data || []).map((r) => ({
    ...r, dueAt: abd ? r.appointmentDeliveryData?.appointment_del_start : r.edd,
  }));
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 2.5, mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>Yet to deliver today</Typography>
          <Typography sx={metaSx}>
            {loading ? 'Loading…' : total
              ? `${num(total)} shipment${total === 1 ? '' : 's'} · earliest due first${total > DUE_LIST_SIZE ? ` · showing ${DUE_LIST_SIZE}` : ''}`
              : 'Everything due today is delivered'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportButton label='Download' extraAndFilter={filter} appointmentRequired={abd} fileName={fileName} />
          <Button size='small' variant='outlined' onClick={() => navigate(listPath)} sx={{ textTransform: 'none', fontFamily: FONT }}>
            View all {abd ? 'ABD' : 'Non-ABD'} today
          </Button>
        </Box>
      </Box>
      {loading ? (
        <TableSkeleton rows={5} columns={dueColumns.length} />
      ) : rows.length ? (
        <Box sx={dataGridWrapSx}>
          <ProzoDataGrid
            columns={dueColumns}
            rows={rows}
            rowCount={rows.length}
            paginationMode='client'
            hideFooterPagination
            getRowId={(r) => r.id || r.awb_number}
            boxShadow={0}
          />
        </Box>
      ) : (
        <Typography sx={{ ...metaSx, textAlign: 'center', py: 3 }}>Nothing left to deliver today.</Typography>
      )}
    </>
  );
}

const TABS = [
  { key: 'abd', label: 'ABD shipments' },
  { key: 'nonAbd', label: 'Non-ABD shipments' },
];

const failedAlert = (queries) => {
  const failed = queries.find((q) => q.error);
  return failed ? <Alert severity='error' sx={{ mb: 2 }}>{errorText(failed.error, 'Could not load the counts. Please refresh.')}</Alert> : null;
};

/** ABD tab: today's appointments, open misses by attribution, what needs attention, and what's still due. */
function AbdTab({ now, reload }) {
  const navigate = useNavigate();
  // order/search counts; each tile sends the same filter as the list it opens, so the numbers match.
  const abdToday = useAppointmentSearch(countQuery(abdTodayFilter(now), reload));
  const abdDelivered = useAppointmentSearch(countQuery([...abdTodayFilter(now), DELIVERED], reload));
  const overdue = useAppointmentSearch(countQuery(buildOverdueFilter(now), reload));
  const noNewDate = useAppointmentSearch(countQuery(NO_NEW_DATE_FILTER, reload));
  const noFirst = useAppointmentSearch(countQuery(noFirstAppointmentFilter(now), reload));
  const openMissed = useAppointmentSearch(countQuery(OPEN_MISSED_FILTER, reload));
  // One count per attribution (fixed list, so the hook order never changes).
  const missByOwner = MISS_ATTRIBUTION_KEYS.map((k) => ({
    key: k, label: MISS_ATTRIBUTION_SHORT[k], color: MISS_ATTRIBUTION[k].color,
    query: useAppointmentSearch(countQuery([...OPEN_MISSED_FILTER, latestMissFilter(k)], reload)), // eslint-disable-line react-hooks/rules-of-hooks
  }));
  const dueFilter = [...abdTodayFilter(now), NOT_DELIVERED];
  const due = useAppointmentSearch({
    extraAndFilter: dueFilter, page: 0, sizePerPage: DUE_LIST_SIZE, reload,
    sort: { field: 'appointmentDeliveryData.appointmentDeliveryStartTime', direction: 'ASC' },
  });

  return (
    <>
      {failedAlert([abdToday, abdDelivered, overdue, noNewDate, noFirst, openMissed, ...missByOwner.map((o) => o.query), due])}
      <Box sx={softCardSx}>
        <CardHeader title='Today' info='Appointment-based delivery. Today = the appointment slot is today. Cancelled shipments are not counted.'
          sub='Appointments today and appointments missed so far' />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Tile withBar accent={PRIMARY_BLUE} valueColor={NAVY} icon={<EventAvailableIcon />} label='Scheduled today'
              hint='Appointment slot today' value={countOf(abdToday)} {...deliveredParts(countOf(abdToday), countOf(abdDelivered))}
              cta='View list' onClick={() => navigate(PTL_PATHS.abdToday)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Tile withBar accent={WARNING} valueColor={NAVY} icon={<EventBusyOutlinedIcon />} label='Missed appointments'
              hint='Missed at least once, still open · by who the latest miss is on' value={countOf(openMissed)}
              {...missParts(countOf(openMissed), missByOwner.map((o) => ({ key: o.key, label: o.label, color: o.color, value: countOf(o.query) })))}
              cta='View missed' onClick={() => navigate(MISSED_PATH)} />
          </Grid>
        </Grid>

        <SubHeading title='Needs attention' sub='Still open, waiting on a slot update or an appointment date' />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Tile tint accent={ERROR} valueColor={ERROR} icon={<ReportProblemOutlinedIcon />} label='Overdue, not closed'
              hint='Slot has passed, no update yet' value={countOf(overdue)}
              cta='View overdue' onClick={() => navigate(PTL_PATHS.overdue)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Tile tint accent={WARNING} valueColor={AMBER_TEXT} icon={<AccessTimeIcon />} label='Missed, no new date'
              hint='Next appointment not booked yet' value={countOf(noNewDate)}
              cta='View missed' onClick={() => navigate(`${PTL_PATHS.overdue}?view=noNewDate`)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Tile tint accent={SUBTLE} valueColor={NAVY} icon={<HourglassEmptyIcon />} label='No first appointment'
              hint={`Not booked yet · last ${NO_FIRST_APPOINTMENT_DAYS} days`} value={countOf(noFirst)}
              cta='View list' onClick={() => navigate(`${PTL_PATHS.overdue}?view=noFirst`)} />
          </Grid>
        </Grid>

        <DueList abd query={due} filter={dueFilter} listPath={PTL_PATHS.abdToday} fileName='ABD_Yet_To_Deliver_Today' />
      </Box>
    </>
  );
}

/** Non-ABD tab: shipments due today (EDD), EDD breaches by attribution, and what's still due. */
function NonAbdTab({ now, reload }) {
  const navigate = useNavigate();
  const today = useAppointmentSearch(countQuery(nonAbdTodayFilter(now), reload, false));
  const delivered = useAppointmentSearch(countQuery([...nonAbdTodayFilter(now), DELIVERED], reload, false));
  const dueFilter = [...nonAbdTodayFilter(now), NOT_DELIVERED];
  const due = useAppointmentSearch({
    extraAndFilter: dueFilter, page: 0, sizePerPage: DUE_LIST_SIZE, reload,
    appointmentRequired: false, sort: { field: 'edd', direction: 'ASC' },
  });

  // Merchant EDD breach API; the server uses merchantOid only for Proship admins.
  const merchantOid = getMerchant()?.id || '';
  const edd = usePtlApi(() => getGeneric(`${apiConstants.PTL_EDD_BREACH_MERCHANT}/summary`, merchantOid ? { tab: 'U', merchantOid } : { tab: 'U' }),
    `${merchantOid}|${reload}`);
  const eddTiles = edd.data?.tiles || {};
  const eddSegments = EDD_OWNER_KEYS.map((k) => ({ key: k, label: EDD_OWNERS[k].short, value: eddTiles[k] || 0, color: EDD_OWNERS[k].color }));

  return (
    <>
      {failedAlert([today, delivered, due])}
      <Box sx={softCardSx}>
        <CardHeader title='Today' info='Delivered without an appointment. Today = the EDD is today. Cancelled shipments are not counted.'
          sub='Shipments due today and EDD breaches right now' />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Tile withBar accent={TEAL} valueColor={NAVY} icon={<LocalShippingOutlinedIcon />} label='Scheduled today'
              hint='EDD today' value={countOf(today)} {...deliveredParts(countOf(today), countOf(delivered))}
              cta='View list' onClick={() => navigate(PTL_PATHS.nonAbdAll, { state: { orderFilter: { eddDate: formatDate(now, 'yyyy-MM-dd') } } })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Tile withBar accent={ERROR} valueColor={NAVY} icon={<AssignmentLateOutlinedIcon />} label='EDD breaches'
              hint='Past EDD, not delivered yet · by attribution' value={apiValue(edd, edd.data?.tabCounts?.U)}
              {...apiParts(edd, eddSegments, 'No breaches')}
              cta='View shipments' onClick={() => navigate(PTL_PATHS.nonAbdAll, { state: { orderFilter: { eddBreached: formatDate(now, 'yyyy-MM-dd') } } })} />
          </Grid>
        </Grid>

        <SubHeading title='EDD breach pendencies' sub='Undelivered past EDD, by who the delay is on' />
        <Grid container spacing={2}>
          {EDD_OWNER_KEYS.map((k) => (
            <Grid item xs={12} sm={6} md={3} key={k}>
              <Tile tint accent={EDD_OWNERS[k].accent} valueColor={EDD_OWNERS[k].color} icon={EDD_OWNER_ICONS[k]} label={EDD_OWNERS[k].label}
                hint={EDD_OWNERS[k].hint} value={apiValue(edd, eddTiles[k])}
                cta='View list' onClick={() => navigate(`${PTL_PATHS.eddBreaches}?owner=${k}`)} />
            </Grid>
          ))}
        </Grid>

        <DueList abd={false} query={due} filter={dueFilter} listPath={PTL_PATHS.nonAbdToday} fileName='Non_ABD_Yet_To_Deliver_Today' />
      </Box>
    </>
  );
}

const apiValue = (state, v) => (state.loading ? undefined : state.error ? null : v ?? 0);

/**
 * PTL Delivery Health landing page, one tab per shipment type. Only the open tab is mounted, so a big brand loads
 * one type's counts at a time. ?tab=nonAbd opens the Non-ABD tab.
 */
export default function PtlToday() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'nonAbd' ? 'nonAbd' : 'abd';
  const [now, setNow] = useState(() => new Date());
  const [reload, setReload] = useState(0);
  const refresh = () => { setNow(new Date()); setReload((r) => r + 1); };
  const changeTab = (_e, v) => setSearchParams(v === 'abd' ? {} : { tab: v }, { replace: true });

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Today's PTL shipments and open appointments">PTL Delivery Health</PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', ...tabBarSx, mb: 2 }}>
        <Tabs value={tab} onChange={changeTab} sx={{ minHeight: 44 }}>
          {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.75 }}>
          <Typography sx={metaSx}>{formatDate(now, 'EEE, dd MMM yyyy')} · updated {formatDate(now, 'h:mm a')}</Typography>
          <ButtonBase onClick={refresh} sx={{ ...linkTextSx, borderRadius: '8px', px: 1, py: 0.5 }}>
            <RefreshIcon sx={{ fontSize: 15 }} /> Refresh
          </ButtonBase>
        </Box>
      </Box>

      {tab === 'abd' ? <AbdTab now={now} reload={reload} /> : <NonAbdTab now={now} reload={reload} />}
    </Box>
  );
}
